/**
 * AccumulatorInstructionsLabel — HTML overlay teaching the bill-stack gestures.
 *
 * Two gesture flags are tracked:
 *   - drag-to-place: player dragged the stack onto a bet zone and the bet
 *                    placed (drop landed on a valid target).
 *   - tap-to-remove: player tapped the stack (no drag) to undo the last bill.
 *
 * Each line collapses out independently as its gesture is demonstrated; once
 * BOTH flags are satisfied, the label is dismissed and never re-shown
 * (persisted in localStorage).
 *
 * Visibility is driven by BettingController: shown while the accumulator has
 * bills AND the player isn't actively dragging the stack, hidden otherwise.
 */

import { gsap } from 'gsap';

export interface AccumulatorInstructionsLabelOptions {
  container: HTMLElement;
}

const STORAGE_KEY = 'sdl_accumulator_tutorial_v1';

interface TutorialFlags {
  dragPlaceUsed: boolean;
  tapRemoveUsed: boolean;
}

export class AccumulatorInstructionsLabel {
  private root: HTMLElement;
  private dragLine: HTMLElement;
  private tapLine: HTMLElement;
  private flags: TutorialFlags;
  private isShown = false;
  private disabled = false;

  constructor(options: AccumulatorInstructionsLabelOptions) {
    AccumulatorInstructionsLabel.injectStyles();
    this.flags = this.loadFlags();

    this.root = document.createElement('div');
    this.root.className = 'accumulator-instructions';
    this.root.style.display = 'none';

    const arrow = document.createElement('div');
    arrow.className = 'accumulator-instructions__arrow';

    const textEl = document.createElement('div');
    textEl.className = 'accumulator-instructions__text';

    this.dragLine = document.createElement('div');
    this.dragLine.className = 'accumulator-instructions__line';
    this.dragLine.textContent = 'DRAG OVER SLOT TO PLACE BET';

    this.tapLine = document.createElement('div');
    this.tapLine.className = 'accumulator-instructions__line';
    this.tapLine.textContent = 'TAP ON BILL TO REMOVE';

    if (this.flags.dragPlaceUsed) this.dragLine.style.display = 'none';
    if (this.flags.tapRemoveUsed) this.tapLine.style.display = 'none';

    textEl.appendChild(this.dragLine);
    textEl.appendChild(this.tapLine);

    // Text first, arrow second — arrow points DOWN at the stack below.
    this.root.appendChild(textEl);
    this.root.appendChild(arrow);
    options.container.appendChild(this.root);
  }

  /**
   * Permanently suppress this label. Used by the guided tutorial, where the
   * coach teaches drag-to-place step-by-step — the auto-popping hints would
   * double up on the coach banner. Hides any current label and no-ops every
   * future show(). (Same contract as WalletInstructionsLabel.disable().)
   */
  disable(): void {
    this.disabled = true;
    this.hide();
  }

  /** Show the label if the player hasn't graduated yet. Idempotent. */
  show(): void {
    if (this.disabled || this.isGraduated() || this.isShown) return;
    this.isShown = true;
    this.root.style.display = 'flex';
    this.root.style.transformOrigin = 'center bottom';
    gsap.fromTo(
      this.root,
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(1.7)' }
    );
  }

  /** Hide the label (fade out). Idempotent. */
  hide(): void {
    if (!this.isShown) return;
    this.isShown = false;
    gsap.to(this.root, {
      opacity: 0,
      duration: 0.2,
      ease: 'power2.in',
      onComplete: () => {
        this.root.style.display = 'none';
      },
    });
  }

  /** Record that the player dragged the stack onto a zone and the bet placed. */
  markDragPlaceUsed(): void {
    if (this.flags.dragPlaceUsed) return;
    this.flags.dragPlaceUsed = true;
    this.persistFlags();
    this.collapseLine(this.dragLine);
    this.maybeGraduate();
  }

  /** Record that the player tapped a bill to remove it from the stack. */
  markTapRemoveUsed(): void {
    if (this.flags.tapRemoveUsed) return;
    this.flags.tapRemoveUsed = true;
    this.persistFlags();
    this.collapseLine(this.tapLine);
    this.maybeGraduate();
  }

  /** Has the player demonstrated both gestures? */
  isGraduated(): boolean {
    return this.flags.dragPlaceUsed && this.flags.tapRemoveUsed;
  }

  destroy(): void {
    gsap.killTweensOf(this.root);
    this.root.remove();
  }

  // ── internals ──────────────────────────────────────────────────────

  private collapseLine(line: HTMLElement): void {
    if (line.style.display === 'none') return;
    gsap.to(line, {
      opacity: 0,
      height: 0,
      marginTop: 0,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
      duration: 0.25,
      ease: 'power2.in',
      onComplete: () => {
        line.style.display = 'none';
      },
    });
  }

  private maybeGraduate(): void {
    if (this.isGraduated()) this.hide();
  }

  private loadFlags(): TutorialFlags {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<TutorialFlags>;
        return {
          dragPlaceUsed: !!parsed.dragPlaceUsed,
          tapRemoveUsed: !!parsed.tapRemoveUsed,
        };
      }
    } catch { /* localStorage unavailable */ }
    return { dragPlaceUsed: false, tapRemoveUsed: false };
  }

  private persistFlags(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.flags));
    } catch { /* noop */ }
  }

  private static stylesInjected = false;
  private static injectStyles(): void {
    if (AccumulatorInstructionsLabel.stylesInjected) return;
    AccumulatorInstructionsLabel.stylesInjected = true;
    const style = document.createElement('style');
    style.textContent = `
      .accumulator-instructions {
        position: fixed;
        right: 16px;
        bottom: 360px;
        display: flex;
        flex-direction: column;
        align-items: center;
        pointer-events: none;
        z-index: 55;
        font-family: 'Bebas Neue', 'Rajdhani', sans-serif;
        will-change: transform, opacity;
        font-size: 0;
        max-width: 40vw;
      }
      .accumulator-instructions__text {
        background: #000000;
        color: #FFFFFF;
        padding: 5px;
        font-size: 16px;
        line-height: 1.25;
        letter-spacing: 0.06em;
        text-align: center;
        white-space: normal;
        max-width: 40vw;
        box-sizing: border-box;
      }
      .accumulator-instructions__line {
        display: block;
        overflow: hidden;
      }
      .accumulator-instructions__line + .accumulator-instructions__line {
        margin-top: 6px;
      }
      .accumulator-instructions__line::before {
        content: '\\2022';
        display: inline-block;
        margin-right: 6px;
        color: #FFFFFF;
      }
      .accumulator-instructions__arrow {
        width: 0;
        height: 0;
        border-left: 16.5px solid transparent;
        border-right: 16.5px solid transparent;
        border-top: 21px solid #000000;
        margin-top: -1px;
      }
    `;
    document.head.appendChild(style);
  }
}
